/**
 * bomLinkSaveWarning.js — S86 (HU-05/CA5.3, RN-05.6): pure decision of whether saving the workflow
 * currently being edited is going to create a NEW VERSION of the library BOM it is linked to, and
 * which version number that will be.
 *
 * Why these three inputs and no others: the version is created by Sun, not by Jupiter, and Sun's
 * trigger (contrato-sun.md §7, deviation 9) is exactly "workflow LINKED + temp load present at
 * save time". `BOM_LIBRARY_EDIT` is part of the condition because without it that same save is
 * rejected with 403 (E-PRM-02), so announcing a version that will never exist would be a lie.
 *
 * @typedef {object} WorkflowBomLink contrato-sun.md §2 `WorkflowBomLinkDto`
 * @property {boolean} linked
 * @property {boolean} embedded
 * @property {string} code
 * @property {number} versionNumber the version the workflow points at
 * @property {number} latestVersionNumber the highest version of that BOM
 */

/**
 * S86: whether the user must be warned, before the save runs, that version n+1 of the linked BOM
 * will be created and that the workflow will point to it.
 *
 * @param {object} params
 * @param {WorkflowBomLink} params.link the workflow's BOM link, as N15 returns it
 * @param {boolean} params.hasTempLoad whether the editor carries a temp BOM load (an edited structure)
 * @param {boolean} params.canEdit whether the user holds `BOM_LIBRARY_EDIT`
 * @param {boolean} params.hasPendingLink S71 (corrección PO 2026-09-09): whether the editor carries a
 * BOM link chosen but not written yet. The structure on screen is then the one of the version about
 * to be LINKED, not an edit of the currently linked BOM: that save links, it does not version.
 * @returns {boolean}
 */
const shouldWarnNewVersionOnSave = ({ link = {}, hasTempLoad = false, canEdit = false, hasPendingLink = false } = {}) => {
  return Boolean(link?.linked) && Boolean(hasTempLoad) && Boolean(canEdit) && !hasPendingLink
}

/**
 * S86: the version number the save will create — the HIGHEST existing version plus one, never the
 * linked one plus one (the workflow may point at an older version while newer ones already exist).
 *
 * @param {WorkflowBomLink} link
 * @returns {number}
 */
const nextVersionNumber = (link = {}) => {
  const highest = Number(link?.latestVersionNumber) || Number(link?.versionNumber) || 0
  return highest + 1
}

export { nextVersionNumber, shouldWarnNewVersionOnSave }
